import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { casesApi, projectsApi } from '../../services/api';
import { ProjectCase, ProjectListItem } from '../../types';
import CaseSidePanel from './components/CaseSidePanel';
import CaseForm from './components/CaseForm';
import LoadingCard from '../../components/LoadingCard';
// @ts-ignore
import DraggableTable from '../../components/DraggableTable';
import { CASE_COLUMNS } from '../../features/cases/components/caseColumns';
import { Plus } from 'lucide-react';

const CasesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<ProjectCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewCaseId, setViewCaseId] = useState<number | null>(null);
  const [editCaseId, setEditCaseId] = useState<number | null>(null);
  const [commentsCaseId, setCommentsCaseId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<number | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');

  const projectIdFromUrl = searchParams.get('project_id');

  const loadCases = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.case_status = statusFilter;
      if (projectFilter) params.project_id = projectFilter;
      if (priorityFilter) params.case_priority = priorityFilter;

      const data = await casesApi.list(params);
      setRows(data.data);
    } catch (error) {
      console.error('Failed to load cases:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectIdFromUrl) {
      const projectId = parseInt(projectIdFromUrl, 10);
      if (!isNaN(projectId)) setProjectFilter(projectId);
    }
  }, [projectIdFromUrl]);

  useEffect(() => { loadCases(); }, [statusFilter, projectFilter, priorityFilter]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await projectsApi.list({});
        setProjects(data.data || []);
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    };
    loadProjects();
  }, []);

  const onKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setViewCaseId(null);
      setEditCaseId(null);
      setCommentsCaseId(null);
      setShowCreateModal(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  const handleCaseCreated = () => {
    setShowCreateModal(false);
    loadCases();
  };

  const columns = CASE_COLUMNS(
    (id) => setViewCaseId(id),
    (id) => setEditCaseId(id),
    (id) => setCommentsCaseId(id),
  );

  return (
    <div className="theme-card rounded-2xl p-3 md:p-4 relative">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold theme-text-primary">Project Cases</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-shadow text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Case
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-white  theme-border rounded-lg p-2  focus:outline-none focus:border-blue-500 focus:ring-0  theme-bg-primary theme-text-primary"
        >
          <option value="">All Statuses</option>
          <option value="ready">Ready</option>
          <option value="working">Working</option>
          <option value="waiting">Waiting</option>
          <option value="reviewing">Reviewing</option>
          <option value="approved">Approved</option>
          <option value="uploading">Uploading</option>
          <option value="closed">Closed</option>
          <option value="additional_request">Additional Request</option>
        </select>

        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value ? Number(e.target.value) : '')}
          className="border border-white  theme-border rounded-lg p-2  focus:outline-none focus:border-blue-500 focus:ring-0  theme-bg-primary theme-text-primary"
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="border border-white  theme-border rounded-lg p-2  focus:outline-none focus:border-blue-500 focus:ring-0  theme-bg-primary theme-text-primary"
        >
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </div>

      {loading && rows.length === 0 ? (
        <LoadingCard title="Project Cases" message="Loading cases..." />
      ) : (
        <DraggableTable
          columns={columns}
          data={rows}
          emptyMessage="No cases found"
        />
      )}

      {viewCaseId && (
        <CaseSidePanel
          caseId={viewCaseId}
          onClose={() => setViewCaseId(null)}
          onUpdate={loadCases}
          mode="view"
          onModeChange={(newMode: 'view' | 'edit' | 'comments') => {
            if (newMode === 'edit') {
              setViewCaseId(null);
              setEditCaseId(viewCaseId);
            }
          }}
        />
      )}
      {editCaseId && (
        <CaseSidePanel
          caseId={editCaseId}
          onClose={() => setEditCaseId(null)}
          onUpdate={loadCases}
          mode="edit"
        />
      )}
      {commentsCaseId && (
        <CaseSidePanel
          caseId={commentsCaseId}
          onClose={() => setCommentsCaseId(null)}
          onUpdate={loadCases}
          mode="comments"
        />
      )}

      {showCreateModal && (
        <CaseForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCaseCreated}
          projects={projects}
          initialProjectId={projectFilter !== '' ? Number(projectFilter) : undefined}
        />
      )}
    </div>
  );
};

export default CasesPage;
